import React, { useState, useEffect } from 'react';
import { Sidebar } from '../includes/sidebar';
import { Topbar } from '../includes/topbar';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '../../components/ui/table';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import {
    Card,
    CardContent,
    CardDescription,
    CardHeader,
    CardTitle,
} from '../../components/ui/card';
import {
    Breadcrumb,
    BreadcrumbItem,
    BreadcrumbLink,
    BreadcrumbList,
    BreadcrumbPage,
    BreadcrumbSeparator,
} from "../../components/ui/breadcrumb";
import {
    Users,
    Search,
    UserPlus,
    Filter,
    MoreVertical,
    Shield,
    Smartphone,
    Lock,
    Unlock,
    RefreshCw,
    Edit,
    Loader2,
    UserX
} from 'lucide-react';
import { useToast } from '../../hooks/use-toast';
import { usersApi, User, UserRole } from '../../api/users.api';
import { UserDialog } from './components/UserDialog';
import { FreezeUserDialog, ConfirmDialog } from './components/UserActions';

export default function UsersPage() {
    const { toast } = useToast();
    const [users, setUsers] = useState<User[]>([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);

    // Dialog States
    const [showUserDialog, setShowUserDialog] = useState(false);
    const [selectedUser, setSelectedUser] = useState<User | null>(null);

    const [showFreezeDialog, setShowFreezeDialog] = useState(false);
    const [freezeUser, setFreezeUser] = useState<{ id: string, name: string } | null>(null);

    // Confirmation States
    const [confirmState, setConfirmState] = useState<{
        open: boolean;
        title: string;
        description: string;
        action: () => Promise<void>;
        loading: boolean;
    }>({
        open: false,
        title: '',
        description: '',
        action: async () => { },
        loading: false,
    });

    useEffect(() => {
        loadUsers();
    }, [page, search]);

    const loadUsers = async () => {
        try {
            setLoading(true);
            const data = await usersApi.getUsers(page, 20, search);
            setUsers(data.users);
            setTotalPages(data.pagination.totalPages);
        } catch (error) {
            console.error(error);
            toast({ title: 'Erè', description: 'Pa kapab chaje lis itilizatè yo', type: 'error' });
        } finally {
            setLoading(false);
        }
    };

    const handleSearch = (e: React.FormEvent) => {
        e.preventDefault();
        setPage(1); // Reset to page 1 on search
        loadUsers();
    };

    const handleEdit = (user: User) => {
        setSelectedUser(user);
        setShowUserDialog(true);
    };

    const handleCreate = () => {
        setSelectedUser(null);
        setShowUserDialog(true);
    };

    const handleFreezeClick = (user: User) => {
        setFreezeUser({ id: user.id, name: user.username });
        setShowFreezeDialog(true);
    };

    const handleUnfreeze = (user: User) => {
        setConfirmState({
            open: true,
            title: `Deglase Kont ${user.username}?`,
            description: "Èske ou sèten ou vle deglase kont sa a? Itilizatè a ap kapab konekte ankò.",
            loading: false,
            action: async () => {
                setConfirmState(prev => ({ ...prev, loading: true }));
                try {
                    await usersApi.unfreezeAccount(user.id);
                    toast({ title: 'Siksè', description: 'Kont deglase', type: 'success' });
                    setConfirmState(prev => ({ ...prev, open: false }));
                    loadUsers();
                } catch (error) {
                    toast({ title: 'Erè', description: 'Pa kapab deglase kont lan', type: 'error' });
                    setConfirmState(prev => ({ ...prev, loading: false }));
                }
            }
        });
    };

    const handleResendPin = (user: User) => {
        setConfirmState({
            open: true,
            title: `Renvoye PIN bay ${user.username}?`,
            description: `Nou pral voye yon nouvo lyen konfigirasyon PIN bay ${user.email}.`,
            loading: false,
            action: async () => {
                setConfirmState(prev => ({ ...prev, loading: true }));
                try {
                    await usersApi.resendPinSetup(user.id);
                    toast({ title: 'Siksè', description: 'Lyen voye avèk siksè', type: 'success' });
                    setConfirmState(prev => ({ ...prev, open: false }));
                } catch (error) {
                    toast({ title: 'Erè', description: 'Echèk anvwa lyen', type: 'error' });
                    setConfirmState(prev => ({ ...prev, loading: false }));
                }
            }
        });
    };

    const getRoleBadge = (role: string) => {
        switch (role) {
            case UserRole.ADMIN: return 'bg-purple-100 text-purple-700';
            case UserRole.SUPPORT: return 'bg-blue-100 text-blue-700';
            case UserRole.AGENT: return 'bg-orange-100 text-orange-700';
            case UserRole.SUPER_AGENT: return 'bg-amber-100 text-amber-700 border border-amber-200';
            case UserRole.CAISSIER: return 'bg-emerald-100 text-emerald-700';
            default: return 'bg-slate-100 text-slate-700';
        }
    };

    return (
        <div className="flex h-screen overflow-hidden">
            <Sidebar />
            <div className="flex-1 flex flex-col overflow-hidden">
                <Topbar />
                <div className="flex-1 overflow-y-auto space-y-4 p-8 pt-6">
                    <div className="flex items-center justify-between space-y-2">
                        <Breadcrumb>
                            <BreadcrumbList>
                                <BreadcrumbItem>
                                    <BreadcrumbLink>Système</BreadcrumbLink>
                                </BreadcrumbItem>
                                <BreadcrumbSeparator />
                                <BreadcrumbItem>
                                    <BreadcrumbPage>Utilisateurs</BreadcrumbPage>
                                </BreadcrumbItem>
                            </BreadcrumbList>
                        </Breadcrumb>
                    </div>

                    <Card>
                        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                            <div className="space-y-1">
                                <CardTitle>Lis Itilizatè</CardTitle>
                                <CardDescription>
                                    Jere kont kliyan, ajan, ak administratè yo.
                                </CardDescription>
                            </div>
                            <Button onClick={handleCreate} className="gap-2 shadow-sm hover:shadow-md transition-all">
                                <UserPlus className="h-4 w-4" />
                                Ajoute Itilizatè
                            </Button>
                        </CardHeader>
                        <CardContent>
                            <div className="flex flex-wrap items-center py-4 gap-2">
                                <div className="relative w-full max-w-sm">
                                    <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                                    <Input
                                        placeholder="Recheche non, email, telefòn..."
                                        className="pl-8"
                                        value={search}
                                        onChange={(e) => setSearch(e.target.value)}
                                    />
                                    {search && (
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            className="absolute right-1 top-1 h-7 w-7 p-0"
                                            onClick={() => setSearch("")}
                                        >
                                            <UserX className="h-3 w-3" />
                                        </Button>
                                    )}
                                </div>
                            </div>
                            <Table>
                                <TableHeader>
                                    <TableRow className="bg-slate-50 hover:bg-slate-50">
                                        <TableHead>Itilizatè</TableHead>
                                        <TableHead>Wòl</TableHead>
                                        <TableHead>Estati</TableHead>
                                        <TableHead>Sekirite</TableHead>
                                        <TableHead className="text-right">Aksyon</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {loading ? (
                                        <TableRow>
                                            <TableCell colSpan={5} className="h-24 text-center">
                                                <div className="flex justify-center items-center gap-2 text-slate-500">
                                                    <Loader2 className="h-5 w-5 animate-spin" />
                                                    Chajman...
                                                </div>
                                            </TableCell>
                                        </TableRow>
                                    ) : users.length === 0 ? (
                                        <TableRow>
                                            <TableCell colSpan={5} className="h-24 text-center text-slate-500">
                                                Pa gen itilizatè yo jwenn match ak rechèch ou a.
                                            </TableCell>
                                        </TableRow>
                                    ) : (
                                        users.map((user) => (
                                            <TableRow key={user.id} className="hover:bg-slate-50/50">
                                                <TableCell>
                                                    <div className="flex flex-col">
                                                        <span className="font-medium text-slate-900">
                                                            {user.prenom || user.nom
                                                                ? `${user.prenom || ''} ${user.nom || ''}`.trim()
                                                                : user.username}
                                                        </span>
                                                        {(user.prenom || user.nom) && (
                                                            <span className="text-xs text-slate-400">@{user.username}</span>
                                                        )}
                                                        <span className="text-xs text-slate-500">{user.email}</span>
                                                        {user.tel && user.tel !== user.username && (
                                                            <span className="text-xs text-slate-400">{user.tel}</span>
                                                        )}
                                                    </div>
                                                </TableCell>
                                                <TableCell>
                                                    <Badge variant="outline" className={`font-normal ${getRoleBadge(user.role)} border-0`}>
                                                        {user.role}
                                                    </Badge>
                                                </TableCell>
                                                <TableCell>
                                                    <div className="flex flex-col gap-1">
                                                        {user.isFrozen ? (
                                                            <Badge variant="destructive" className="w-fit text-[10px] px-1.5 py-0.5 h-5">
                                                                Glase
                                                            </Badge>
                                                        ) : (
                                                            <Badge variant="outline" className="w-fit text-[10px] px-1.5 py-0.5 h-5 bg-green-50 text-green-700 border-green-200">
                                                                Aktif
                                                            </Badge>
                                                        )}
                                                        {user.isFrozen && user.frozenReason && (
                                                            <span className="text-[10px] text-red-500 max-w-[150px] truncate" title={user.frozenReason}>
                                                                {user.frozenReason}
                                                            </span>
                                                        )}
                                                    </div>
                                                </TableCell>
                                                <TableCell>
                                                    <div className="flex items-center gap-2">
                                                        {user.twoFactorEnabled ? (
                                                            <div className="p-1 bg-green-100 rounded text-green-600" title="2FA Aktive">
                                                                <Shield className="h-3 w-3" />
                                                            </div>
                                                        ) : (
                                                            <div className="p-1 bg-slate-100 rounded text-slate-400" title="2FA Dezaktive">
                                                                <Shield className="h-3 w-3" />
                                                            </div>
                                                        )}
                                                    </div>
                                                </TableCell>
                                                <TableCell className="text-right">
                                                    <DropdownMenu>
                                                        <DropdownMenuTrigger asChild>
                                                            <Button variant="ghost" className="h-8 w-8 p-0">
                                                                <span className="sr-only">Open menu</span>
                                                                <MoreVertical className="h-4 w-4" />
                                                            </Button>
                                                        </DropdownMenuTrigger>
                                                        <DropdownMenuContent align="end">
                                                            <DropdownMenuLabel>Aksyon</DropdownMenuLabel>
                                                            <DropdownMenuItem onClick={() => handleEdit(user)}>
                                                                <Edit className="mr-2 h-4 w-4" />
                                                                Modifye
                                                            </DropdownMenuItem>
                                                            <DropdownMenuItem onClick={() => handleResendPin(user)}>
                                                                <RefreshCw className="mr-2 h-4 w-4" />
                                                                Renvoye PIN
                                                            </DropdownMenuItem>
                                                            <DropdownMenuSeparator />
                                                            {user.isFrozen ? (
                                                                <DropdownMenuItem onClick={() => handleUnfreeze(user)} className="text-green-600">
                                                                    <Unlock className="mr-2 h-4 w-4" />
                                                                    Deglase Kont
                                                                </DropdownMenuItem>
                                                            ) : (
                                                                <DropdownMenuItem onClick={() => handleFreezeClick(user)} className="text-red-600">
                                                                    <Lock className="mr-2 h-4 w-4" />
                                                                    Glase Kont
                                                                </DropdownMenuItem>
                                                            )}
                                                        </DropdownMenuContent>
                                                    </DropdownMenu>
                                                </TableCell>
                                            </TableRow>
                                        ))
                                    )}
                                </TableBody>
                            </Table>
                        </CardContent>
                        {/* Pagination - Simplified */}
                        <div className="p-4 border-t flex items-center justify-between">
                            <span className="text-sm text-slate-500">
                                Paj {page} sou {totalPages}
                            </span>
                            <div className="flex gap-2">
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => setPage(p => Math.max(1, p - 1))}
                                    disabled={page === 1}
                                >
                                    Precedan
                                </Button>
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                                    disabled={page === totalPages}
                                >
                                    Sivan
                                </Button>
                            </div>
                        </div>
                    </Card>
                </div>
            </div>

            {/* Dialogs */}
            <UserDialog
                open={showUserDialog}
                onOpenChange={setShowUserDialog}
                user={selectedUser}
                onSuccess={loadUsers}
            />

            <FreezeUserDialog
                open={showFreezeDialog}
                onOpenChange={setShowFreezeDialog}
                userId={freezeUser?.id || null}
                userName={freezeUser?.name || ''}
                onSuccess={loadUsers}
            />

            <ConfirmDialog
                open={confirmState.open}
                onOpenChange={(open: boolean) => setConfirmState(prev => ({ ...prev, open }))}
                title={confirmState.title}
                description={confirmState.description}
                onConfirm={confirmState.action}
                loading={confirmState.loading}
            />
        </div>
    );
}
